import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--manufacturers-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "parentIdInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editIdInput"
    ]

    static values = {
        deviceTypeId: String
    }

    connect() {
        this.initializeTable()
        this.loadManufacturers()
        this._setupRowDoubleClick()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _setupRowDoubleClick() {
        if (!this.hasTableTarget) return
        this.tableTarget.addEventListener('dblclick', (e) => {
            const tr = e.target.closest('tbody tr')
            if (!tr) return
            const argusTable = this.application.getControllerForElementAndIdentifier(this.tableTarget, 'argus--table')
            if (!argusTable) return
            const rows = Array.from(this.tableTarget.querySelectorAll('tbody tr'))
            const index = rows.indexOf(tr)
            const data = argusTable.getData()
            const row = data[index]
            if (row && row.id) {
                window.location.href = `/settings/device-types/${this.deviceTypeIdValue}/manufacturers/${row.id}`
            }
        })
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadManufacturers() {
        if (!this.deviceTypeIdValue) {
            console.error('No device type ID provided')
            return
        }

        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch(`/api/device_types/device_manufacturer/list?device_type_id=${this.deviceTypeIdValue}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch manufacturers')
            }

            const data = await response.json()
            this.populateTable(data.manufacturers || [])
        } catch (error) {
            console.error('Error loading manufacturers:', error)
            sendFailureNotification('Failed to load manufacturers')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(manufacturers) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(manufacturers)
            }
        }
    }

    openCreateModal() {
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        
        // Set parent ID
        if (this.hasParentIdInputTarget) {
            this.parentIdInputTarget.value = this.deviceTypeIdValue
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/device_types/device_manufacturer/create', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to create manufacturer')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.createModalTarget)
                if (modal) {
                    this.createModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadManufacturers()

            sendSuccessNotification('Manufacturer created successfully')
        } catch (error) {
            console.error('Error creating manufacturer:', error)
            sendFailureNotification('Failed to create manufacturer')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const manufacturerId = button.dataset.manufacturerId

        // Fetch manufacturer details
        this.fetchManufacturerDetails(manufacturerId)
    }

    async fetchManufacturerDetails(manufacturerId) {
        try {
            const response = await fetch(`/api/device_types/device_manufacturer/describe?id=${manufacturerId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch manufacturer details')
            }

            const data = await response.json()
            const manufacturer = data.device_type_definitions[0]

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = manufacturer.name || ''
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = manufacturer.id
            }

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching manufacturer details:', error)
            sendFailureNotification('Failed to load manufacturer details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/device_types/device_manufacturer/update', {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to update manufacturer')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.editModalTarget)
                if (modal) {
                    this.editModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadManufacturers()

            sendSuccessNotification('Manufacturer updated successfully')
        } catch (error) {
            console.error('Error updating manufacturer:', error)
            sendFailureNotification('Failed to update manufacturer')
        }
    }

    async deleteManufacturer(event) {
        event.preventDefault()

        const button = event.currentTarget
        const manufacturerId = button.dataset.manufacturerId

        // Get manufacturer name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const manufacturerName = nameCell ? nameCell.textContent : 'this manufacturer'

        if (!confirm(`Are you sure you want to delete ${manufacturerName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', manufacturerId)

        try {
            const response = await fetch('/api/device_types/device_manufacturer/delete', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to delete manufacturer')
            }

            // Reload table
            await this.loadManufacturers()

            sendSuccessNotification('Manufacturer deleted successfully')
        } catch (error) {
            console.error('Error deleting manufacturer:', error)
            sendFailureNotification('Failed to delete manufacturer')
        }
    }

    viewModels(event) {
        event.preventDefault()

        const button = event.currentTarget
        const manufacturerId = button.dataset.manufacturerId

        // Navigate to models page
        window.location.href = `/settings/device-types/${this.deviceTypeIdValue}/manufacturers/${manufacturerId}`
    }
}
